import { useState } from "react";
import spadeIcon from "../../../assets/img/cardsIcons/spade.png";
import heartIcon from "../../../assets/img/cardsIcons/heart.png";
import diamondIcon from "../../../assets/img/cardsIcons/diamond.png";
import clubIcon from "../../../assets/img/cardsIcons/club.png";
import Collapse from "../../../components/Collapse";
import { formatBook } from "../../../utils/formatOdds";

function formatStake(num) {
  if (num == null || num === undefined) return "0";
  const n = Number(num);
  if (n >= 100000) return `${n / 100000}L`;
  if (n >= 1000) return `${n / 1000}K`;
  return String(n);
}

/**
 * Teenpatti Poison 20-20 bet table (same reference markup as joker20; the reference labels the side market "Joker") — reference markup (.teen1daycasino-container mt-2 inside .casino-detail):
 * left = Player A/B back+lay rows, right (.joker-other) = Poison Even/Odd/Red/Black and suit bets.
 * Book spans follow the reference directive: book-green when > 0 else book-red, and a suspended
 * side box with a book gets "lock-top" (tenexch: padlock at the top only with a liability). `children` (the Last Result title + strip) render inside the container,
 * after the right column, as in the reference.
 */
const BetTablePoison20 = ({ data = [], onBetClick, exposures = {}, children }) => {
  // Reference = independent Bootstrap collapses: undefined = never toggled, then true (open) / false (closed).
  const [ranges, setRanges] = useState({});

  const toggleRange = (rangeId) => {
    setRanges((prev) => ({ ...prev, [rangeId]: !prev[rangeId] }));
  };


  const find = (nat) => data.find((i) => i.nat === nat) || {};
  const findPlayer = (nat) => data.find((i) => i.nat === nat && i.subtype === "poison20") || find(nat);

  const playerA = findPlayer("Player A");
  const playerB = findPlayer("Player B");
  const poisonEven = find("Poison Even");
  const poisonOdd = find("Poison Odd");
  const poisonRed = find("Poison Red");
  const poisonBlack = find("Poison Black");
  const poisonSpade = find("Poison Spade");
  const poisonHeart = find("Poison Heart");
  const poisonDiamond = find("Poison Diamond");
  const poisonClub = find("Poison Club");

  const getExposure = (nat) => {
    const exp = exposures[nat] ?? exposures[nat?.toLowerCase()] ?? exposures[nat?.trim()] ?? 0;
    return Number(exp) || 0;
  };

  const book = (nat) => {
    const exp = getExposure(nat);
    return {
      cls: exp > 0 ? "book-green" : "book-red",
      text: formatBook(exp),
    };
  };

  // Reference: a box is suspended whenever gstatus is not OPEN (a 0 rate on an open market is shown, not locked;
  // clicks on a 0 / missing rate are ignored).
  const isSuspended = (item) => String(item?.gstatus || "").toUpperCase() !== "OPEN";

  const rangeToggle = (rangeId) => ({
    role: "button",
    tabIndex: 0,
    onClick: (e) => {
      e.stopPropagation();
      toggleRange(rangeId);
    },
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggleRange(rangeId);
      }
    },
    "data-toggle": "collapse",
    "data-target": `#${rangeId}`,
  });

  const renderRange = (item, rangeId) => (
    <Collapse id={rangeId} open={!!ranges[rangeId]} className="icon-range">
      R:<span>{formatStake(item?.min)}</span>-<span>{formatStake(item?.max)}</span>
    </Collapse>
  );

  const renderPlayerRow = (item, label, rangeIdx) => {
    const backLocked = isSuspended(item);
    const layLocked = isSuspended(item);
    const rangeId = `range${rangeIdx}`;
    const isOpen = !!ranges[rangeId];
    const bk = book(label);

    return (
      <div className="casino-box-row">
        <div className="casino-nation-name">
          <b>{label}</b>{" "}
          <div className="float-right">
            <span className={`mr-2 ${bk.cls}`}>{bk.text}</span>{" "}
            <i
              {...rangeToggle(rangeId)}
              aria-expanded={isOpen ? "true" : "false"}
              className={`fas fa-info-circle${isOpen ? "" : " collapsed"}`}
            />{" "}
            {renderRange(item, rangeId)}
          </div>
        </div>{" "}
        <div className="casino-bl-box">
          <div
            className={`back casino-bl-box-item${backLocked ? " suspended" : ""}`}
            onClick={() => !backLocked && item.b && onBetClick(item.b, label, item, "back")}
          >
            <span className="casino-box-odd">{item.b || 0}</span>
          </div>{" "}
          <div
            className={`lay casino-bl-box-item${layLocked ? " suspended" : ""}`}
            onClick={() => !layLocked && item.l && onBetClick(item.l, label, item, "lay")}
          >
            <span className="casino-box-odd">{item.l || 0}</span>
          </div>
        </div>
      </div>
    );
  };

  /** Back-only cell of the Poison section: label/icons (or odds) + book, with its range toggle below. */
  const renderBackCell = (item, rangeIdx, { label, icons } = {}) => {
    const locked = isSuspended(item);
    const rangeId = `range${rangeIdx}`;
    const nat = item?.nat || label || "";
    const bk = book(nat);

    return (
      <div className="casino-bl-box">
        <div
          className={`back casino-bl-box-item${icons ? " casino-card-img" : ""}${locked ? (getExposure(nat) ? " suspended lock-top" : " suspended") : ""}`}
          onClick={() => !locked && item.b && onBetClick(item.b, nat, item, "back")}
        >
          {icons ? (
            <span>
              <img src={icons[0]} alt="" /> <img src={icons[1]} alt="" />
            </span>
          ) : (
            <span className="casino-box-odd">{label ?? (item.b || 0)}</span>
          )}{" "}
          <span className={bk.cls}>{bk.text}</span>
        </div>{" "}
        <div className="text-right casino-rb-box-player-range w-100 mt-1">
          <i
            {...rangeToggle(rangeId)}
            className={`fas fa-info-circle float-right${ranges[rangeId] === false ? " collapsed" : ""}`}
          />{" "}
          {renderRange(item, rangeId)}
        </div>
      </div>
    );
  };

  const suitHead = (icon) => (
    <div className="casino-bl-box">
      <div className="casino-bl-box-item casino-card-img">
        <img src={icon} alt="" />
      </div>
    </div>
  );

  return (
    <div className="teen1daycasino-container mt-2">
      <div className="teen1dayleft">
        {renderPlayerRow(playerA, "Player A", 1)}{" "}
        {renderPlayerRow(playerB, "Player B", 2)}
      </div>{" "}
      <div className="teen1daycenter" />{" "}
      <div className="teen1dayright joker-other">
        <div>
          <div className="casino-box-row casino-odds">
            <div className="text-left w-100">
              <b className="text-playerb">Joker</b>
            </div>
          </div>{" "}
          <div className="casino-box-row">
            <div className="casino-bl-box"><b>{poisonEven.b || 0}</b></div>{" "}
            <div className="casino-bl-box"><b>{poisonOdd.b || 0}</b></div>{" "}
            <div className="casino-bl-box"><b>{poisonRed.b || 0}</b></div>{" "}
            <div className="casino-bl-box"><b>{poisonBlack.b || 0}</b></div>
          </div>{" "}
          <div className="casino-box-row">
            {renderBackCell(poisonEven, 3, { label: "Even" })}{" "}
            {renderBackCell(poisonOdd, 4, { label: "Odd" })}{" "}
            {renderBackCell(poisonRed, 5, { icons: [heartIcon, diamondIcon] })}{" "}
            {renderBackCell(poisonBlack, 6, { icons: [spadeIcon, clubIcon] })}
          </div>
        </div>{" "}
        <div className="mt-1">
          <div className="casino-box-row">
            {suitHead(spadeIcon)} {suitHead(heartIcon)} {suitHead(diamondIcon)} {suitHead(clubIcon)}
          </div>{" "}
          <div className="casino-box-row">
            {renderBackCell(poisonSpade, 7)} {renderBackCell(poisonHeart, 8)} {renderBackCell(poisonDiamond, 9)}{" "}
            {renderBackCell(poisonClub, 10)}
          </div>
        </div>
      </div>{" "}
      {children}
    </div>
  );
};

export default BetTablePoison20;
